import {
  APPLICABILITY_STATUSES,
  PRIORITIES,
  SOURCE_TYPES,
  UPDATE_TYPES,
  type ApplicabilityStatus,
} from '@ditto/shared';
import { getGeographyFlag, getGeographyLabel } from '@/shared/geography';
import {
  Ban,
  CircleDashed,
  Compass,
  Copy,
  Gem,
  Minus,
  Rss,
  Shield,
  ShieldAlert,
  Star,
} from 'lucide-react';
import ApplicableIcon from '@/assets/applicable.svg?react';
import NotApplicableIcon from '@/assets/not_applicable.svg?react';
import priorityHighImg from '@/assets/priority-high.png';
import priorityMediumImg from '@/assets/priority-medium.png';
import priorityLowImg from '@/assets/priority-low.png';
import type { ReactNode } from 'react';

// Update type labels (new, revision, withdrawal, etc.)
export const UPDATE_TYPE_LABELS: Record<string, string> = {
  new: 'New',
  revision: 'Revision',
  withdrawal: 'Withdrawal',
  status_change: 'Status change',
  news: 'News',
  unknown: 'Unknown',
};

export const UPDATE_TYPE_COLORS: Record<
  string,
  'green' | 'blue' | 'yellow' | 'red' | 'purple'
> = {
  new: 'green',
  revision: 'blue',
  withdrawal: 'red',
  status_change: 'yellow',
  news: 'purple',
  unknown: 'purple',
};

// Reference type labels (regulation, guidance, standard, news, etc.)
export const SOURCE_TYPE_LABELS: Record<string, string> = {
  regulation: 'Regulation',
  guidance: 'Guidance',
  standard: 'Standard',
  news: 'News',
  unknown: 'Unknown',
};

/**
 * @deprecated Use getGeographyLabel() from @/shared/geography instead.
 * Kept as a proxy for existing code that reads GEOGRAPHY_LABELS[code].
 */
export const GEOGRAPHY_LABELS = new Proxy<Record<string, string>>(
  {},
  {
    get: (target, key) => {
      if (typeof key !== 'string') return Reflect.get(target, key);
      return getGeographyLabel(key);
    },
  }
);

/**
 * @deprecated Use getGeographyFlag() from @/shared/geography instead.
 * Kept as a proxy for existing code that reads GEOGRAPHY_FLAG_IMAGES[code].
 */
export const GEOGRAPHY_FLAG_IMAGES = new Proxy<
  Record<string, string | undefined>
>(
  {},
  {
    get: (target, key) => {
      if (typeof key !== 'string') return Reflect.get(target, key);
      return getGeographyFlag(key);
    },
  }
);

export const STATUS_LABELS: Record<string, string> = {
  to_review: 'To review',
  applicable: 'Applicable',
  not_applicable: 'Not applicable',
  out_of_scope: 'Out of scope',
};

export const PRIORITY_LABELS: Record<string, string> = {
  urgent: 'Urgent priority',
  high: 'High priority',
  medium: 'Medium priority',
  low: 'Low priority',
  none: 'No priority',
};

// Update type badge configs matching UpdateTypeBadge in columns.tsx
const UPDATE_TYPE_BADGE: Record<string, { icon: ReactNode; bgClass: string }> =
  {
    new: { icon: <Star className="h-3 w-3" />, bgClass: 'bg-green-100' },
    revision: { icon: <Copy className="h-3 w-3" />, bgClass: 'bg-yellow-100' },
    withdrawal: { icon: <Ban className="h-3 w-3" />, bgClass: 'bg-red-100' },
    status_change: {
      icon: <span className="font-bold">»</span>,
      bgClass: 'bg-blue-100',
    },
    news: { icon: <Rss className="h-3 w-3" />, bgClass: 'bg-blue-100' },
  };

// Filter options for updates list (My updates / Audit — excludes `news`; see DITTO-1328)
export const UPDATE_TYPE_OPTIONS = UPDATE_TYPES.filter(
  t => t !== 'unknown'
).map(value => {
  const badge = UPDATE_TYPE_BADGE[value];
  const label = UPDATE_TYPE_LABELS[value] ?? value;
  return {
    label,
    value,
    renderContent: badge ? (
      <span
        className={`inline-flex items-center gap-1 h-6 px-2 rounded-md text-xs font-medium text-neutral-900 border-transparent shadow-none ${badge.bgClass}`}
      >
        {badge.icon}
        {label}
      </span>
    ) : undefined,
  };
});

const STATUS_ICONS: Record<string, ReactNode> = {
  to_review: <CircleDashed className="h-3.5 w-3.5 shrink-0 text-neutral-400" />,
  applicable: <ApplicableIcon className="h-4 w-4 shrink-0 text-neutral-900" />,
  not_applicable: (
    <NotApplicableIcon className="h-4 w-4 shrink-0 text-neutral-900" />
  ),
  out_of_scope: <Ban className="h-3.5 w-3.5 shrink-0 text-neutral-400" />,
};

export const STATUS_OPTIONS = APPLICABILITY_STATUSES.map(value => ({
  label: STATUS_LABELS[value] ?? value,
  value,
  renderPrefix: STATUS_ICONS[value],
}));

/** Statuses shown in Audit Table (mode=audit). Others are hidden; new statuses stay hidden until added here. */
export const AUDIT_TABLE_VISIBLE_APPLICABILITY_STATUSES = [
  'applicable',
  'not_applicable',
] as const satisfies readonly ApplicabilityStatus[];

export const AUDIT_TABLE_STATUS_OPTIONS =
  AUDIT_TABLE_VISIBLE_APPLICABILITY_STATUSES.map(value => ({
    label: STATUS_LABELS[value] ?? value,
    value,
    renderPrefix: STATUS_ICONS[value],
  }));

const PRIORITY_PREFIXES: Record<string, ReactNode> = {
  urgent: <ShieldAlert className="h-3.5 w-3.5 shrink-0" />,
  high: <img src={priorityHighImg} alt="" className="h-3.5 w-3.5 shrink-0" />,
  medium: (
    <img src={priorityMediumImg} alt="" className="h-3.5 w-3.5 shrink-0" />
  ),
  low: <img src={priorityLowImg} alt="" className="h-3.5 w-3.5 shrink-0" />,
  none: <Minus className="h-3.5 w-3.5 shrink-0 text-neutral-500" />,
};

export const PRIORITY_OPTIONS = PRIORITIES.map(value => ({
  label: PRIORITY_LABELS[value] ?? value,
  value,
  renderPrefix: PRIORITY_PREFIXES[value],
}));

// Document type badge configs matching DocumentTypeBadge in columns.tsx
const SOURCE_TYPE_BADGE: Record<string, { icon: ReactNode; bgClass: string }> =
  {
    regulation: {
      icon: <Shield className="h-3 w-3" />,
      bgClass: 'bg-magenta-100',
    },
    guidance: {
      icon: <Compass className="h-3 w-3" />,
      bgClass: 'bg-purple-100',
    },
    standard: { icon: <Gem className="h-3 w-3" />, bgClass: 'bg-orange-100' },
    news: { icon: <Rss className="h-3 w-3" />, bgClass: 'bg-blue-100' },
  };

// Filter options for references/preferences dialog
export const SOURCE_TYPE_OPTIONS = SOURCE_TYPES.filter(
  t => t !== 'unknown'
).map(value => {
  const badge = SOURCE_TYPE_BADGE[value];
  const label = SOURCE_TYPE_LABELS[value] ?? value;
  return {
    label,
    value,
    renderContent: badge ? (
      <span
        className={`inline-flex items-center gap-1.5 h-6 px-2 rounded-lg text-xs font-medium text-neutral-900 tracking-[0.18px] ${badge.bgClass}`}
      >
        {badge.icon}
        {label}
      </span>
    ) : undefined,
  };
});

// Webpage type labels (for admin back office source management)
export const WEBPAGE_TYPE_LABELS: Record<string, string> = {
  feed: 'Feed',
  listing: 'Listing',
  single_document: 'Single document',
  unknown: 'Unknown',
};

export const WEBPAGE_TYPE_OPTIONS = [
  { label: 'Feed', value: 'feed' },
  { label: 'Listing', value: 'listing' },
  { label: 'Single document', value: 'single_document' },
  { label: 'Unknown', value: 'unknown' },
];
